import { useEffect, useState } from 'react';
import { Panel } from '../components/Card';
import { ThemeSwitcher } from '../components/ThemeSwitcher';
import { Button } from '../components/Button';
import { useAuthStore } from '../lib/auth';
import { apiJson } from '../lib/multiplayer';

export function SettingsRoute() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const isOwner = Boolean(user?.canAdmin && user.username === 'FM');
  async function downloadBackup() {
    const response = await fetch('/api/owner/export');
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ccc-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
  return (
    <div className="min-h-full w-full max-w-[1100px] mx-auto px-5 md:px-10 py-6 pb-12 flex flex-col gap-5">
      <div>
        <h1 className="font-display text-3xl font-bold">Settings</h1>
        <p className="text-sm text-ink-muted">Account and appearance.</p>
      </div>
      <div className="grid lg:grid-cols-[1fr_1.3fr] gap-5">
        <Panel className="p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-muted mb-3">
            Account
          </h2>
          {user ? (
            <div className="space-y-4">
              <div>
                <div className="font-display text-2xl font-bold">{user.displayName}</div>
                <p className="text-sm text-ink-muted">
                  {user.isSupporter ? 'Supporter Themes unlocked.' : 'Member account.'}
                </p>
              </div>
              <Button variant="secondary" onClick={() => void logout()}>
                Log out
              </Button>
              {isOwner && (
                <Button onClick={() => void downloadBackup()}>
                  Download site data
                </Button>
              )}
            </div>
          ) : (
            <p className="text-sm text-ink-muted">
              Log in or join to save supporter unlocks and play other players.
            </p>
          )}
        </Panel>
        <Panel className="p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-muted mb-4">
            Theme
          </h2>
          <ThemeSwitcher isSupporter={Boolean(user?.isSupporter)} />
        </Panel>
      </div>
      {isOwner && <OwnerKofiPanel />}
    </div>
  );
}

interface KofiConfig {
  tokenConfigured: boolean;
  appEnv: string;
}

interface KofiTestResult {
  ok: boolean;
  supporterUnlocked?: boolean;
  codeMatched?: string | null;
  error?: string;
  message?: string;
}

function OwnerKofiPanel() {
  const [config, setConfig] = useState<KofiConfig | null>(null);
  const [code, setCode] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<KofiTestResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiJson<KofiConfig>('/api/owner/kofi-config')
      .then((d) => {
        if (alive) setConfig(d);
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      });
    return () => {
      alive = false;
    };
  }, []);

  const webhookUrl =
    typeof window !== 'undefined' ? `${window.location.origin}/api/webhooks/kofi` : '';

  async function copyUrl() {
    await navigator.clipboard?.writeText(webhookUrl).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  async function runTest() {
    setRunning(true);
    setResult(null);
    setError(null);
    try {
      const body = code.trim() ? { code: code.trim() } : {};
      const data = await apiJson<KofiTestResult>('/api/owner/test-kofi-webhook', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Test failed.');
    } finally {
      setRunning(false);
    }
  }

  return (
    <Panel className="p-5">
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-muted">
          Ko-fi (owner)
        </h2>
        {config && (
          <span className="text-xs text-ink-muted">
            env: <span className="text-ink">{config.appEnv}</span>
            {' · '}
            token:{' '}
            <span className={config.tokenConfigured ? 'text-success' : 'text-danger'}>
              {config.tokenConfigured ? 'configured' : 'missing'}
            </span>
          </span>
        )}
      </div>

      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wider text-ink-muted">Webhook URL</div>
        <div className="flex flex-wrap gap-2 items-stretch">
          <input
            readOnly
            value={webhookUrl}
            className="flex-1 min-w-0 h-10 rounded-md border border-ink/10 bg-surface-2 px-3 text-sm font-mono focus-ring"
          />
          <Button size="sm" variant="secondary" onClick={() => void copyUrl()}>
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <p className="text-xs text-ink-muted">
          Paste this into your Ko-fi dashboard at ko-fi.com/manage/webhooks. The URL points at the
          API worker behind whatever Pages site you are looking at — switch deployments and reopen
          this page to get the right URL.
        </p>
      </div>

      <div className="mt-5 space-y-2">
        <div className="text-xs uppercase tracking-wider text-ink-muted">Test webhook</div>
        <div className="flex flex-wrap gap-2 items-stretch">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="CCC-XXXXXXXXXX (optional)"
            className="flex-1 min-w-0 h-10 rounded-md border border-ink/10 bg-surface px-3 text-sm font-mono focus-ring"
          />
          <Button size="sm" onClick={() => void runTest()} disabled={running}>
            {running ? 'Sending…' : 'Run test'}
          </Button>
        </div>
        <p className="text-xs text-ink-muted">
          Synthesizes a Ko-fi payload using this worker&apos;s verification token and runs it
          through the same handler the live webhook uses. Leave the code blank to confirm the token
          path; paste any user&apos;s active support code to confirm the unlock path.
        </p>
        {error && (
          <div className="text-sm text-danger">{error}</div>
        )}
        {result && (
          <div
            className={`rounded-md border p-3 text-sm ${
              result.ok
                ? 'border-success/30 bg-success/5 text-ink'
                : 'border-danger/30 bg-danger/5 text-ink'
            }`}
          >
            <div className="font-semibold mb-1">
              {result.ok ? 'OK' : `Failed: ${result.error}`}
            </div>
            {result.ok && (
              <>
                <div>
                  Unlock:{' '}
                  <span className="font-semibold">
                    {result.supporterUnlocked ? 'yes' : 'no'}
                  </span>
                </div>
                <div>
                  Code matched:{' '}
                  <span className="font-mono">{result.codeMatched ?? 'none'}</span>
                </div>
                {!result.supporterUnlocked && result.codeMatched && (
                  <div className="mt-1 text-xs text-ink-muted">
                    Token verified, but no user had that support code (or they were already a
                    supporter).
                  </div>
                )}
                {!result.supporterUnlocked && !result.codeMatched && (
                  <div className="mt-1 text-xs text-ink-muted">
                    Token verified. No support code was found in the test payload — that is
                    expected when the code field is blank.
                  </div>
                )}
              </>
            )}
            {!result.ok && result.message && (
              <div className="text-xs text-ink-muted mt-1">{result.message}</div>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
}
